import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  MenuItem,
  Grid
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';
import SearchIcon from '@mui/icons-material/Search';
import SpeedIcon from '@mui/icons-material/Speed';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BlockIcon from '@mui/icons-material/Block';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { facturaPosPath, pedidoPosPath, ROUTES } from '../../config/routes';
import { useSnackbar } from 'notistack';
import cotizacionVentaService, { CotizacionVenta } from '../../services/cotizacionVentaService';
import PDFViewer from '../../components/shared/PDFViewer';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatFechaVencimDisplay } from '../../utils/facturaVentaFechas';

const Cotizaciones: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();

  const [items, setItems] = useState<CotizacionVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales } = useSucursalesVentaFiltro();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedCotizacion, setSelectedCotizacion] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; cotizacionId: number } | null>(null);

  const fetchCotizaciones = useCallback(async (
    pageNumber: number,
    pageSize: number,
    search: string,
    estado: string,
    desde: string,
    hasta: string,
    nroSuc: number | null
  ) => {
    try {
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const params: Parameters<typeof cotizacionVentaService.getCotizacionesVenta>[0] = {
        pageNumber: pageNumber + 1,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde ? new Date(desde) : undefined,
        fechaHasta: hasta ? new Date(hasta) : undefined,
        nroSuc: nroSuc != null && nroSuc > 0 ? nroSuc : undefined,
      };
      if (estado) params.estado = estado;

      const response = await cotizacionVentaService.getCotizacionesVenta(params);
      setItems(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      const message = getApiErrorMessage(error, 'Error al cargar las cotizaciones');
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchCotizaciones(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroNroSuc);
  }, [page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroNroSuc, fetchCotizaciones]);

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    const validRowsPerPage = Math.max(1, Math.min(20, newRowsPerPage));
    setRowsPerPage(validRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNuevaCotizacion = () => navigate(ROUTES.ventas.cotizaciones.pos);

  const handleCotizacionClick = (cotizacionId: number) => {
    navigate(`/ventas/cotizaciones/${cotizacionId}/ver`);
  };

  const _handleEdit = (cotizacionId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/ventas/cotizaciones/${cotizacionId}/editar`);
  };

  const handleAbrirCotizacionPos = (cotizacionId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    navigate(`/ventas/cotizaciones/${cotizacionId}/editar`);
  };

  const handleFacturar = (cotizacionId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(facturaPosPath({ cotizacionId }));
  };

  const handlePreparar = (cotizacionId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(pedidoPosPath({ cotizacionId }));
  };

  const handleAnular = async (cotizacionId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (window.confirm('¿Está seguro que desea anular esta cotización?')) {
      try {
        await cotizacionVentaService.anularCotizacion(cotizacionId);
        enqueueSnackbar('Cotización anulada correctamente', { variant: 'success' });
        fetchCotizaciones(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroNroSuc);
      } catch (error: unknown) {
        const message = getApiErrorMessage(error, 'Error al anular la cotización');
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
      }
    }
  };

  const handleImprimirPDF = async (cotizacionId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      setPdfLoading(cotizacionId);
      const blob = await cotizacionVentaService.getCotizacionVentaPDF(cotizacionId);
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedCotizacion(cotizacionId);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al generar el PDF de la cotización';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setSelectedCotizacion(null);
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, cotizacionId: number) => {
    event.stopPropagation();
    setKebabAnchor({ el: event.currentTarget, cotizacionId });
  };

  const handleKebabClose = () => setKebabAnchor(null);
  const currentKebabCotizacion = kebabAnchor
    ? items.find((c) => c.nroFormulario === kebabAnchor.cotizacionId)
    : null;

  const calcularTotal = (cotizacion: CotizacionVenta): number => {
    if (cotizacion.importeTotal !== undefined && cotizacion.importeTotal !== null) {
      return cotizacion.importeTotal;
    }
    return cotizacion.detalles?.reduce((sum, d) => {
      const subtotal = d.subtotal || (d.precioUnitario || 0) * (d.cantidad || 0);
      return sum + subtotal;
    }, 0) || 0;
  };

  const esCotizacionEditable = (cotizacion: CotizacionVenta): boolean => {
    const estado = cotizacion.estado || 'Nuevo';
    return estado !== 'Facturado' && estado !== 'Preparado' && estado !== 'Anulado';
  };

  const puedeEditarOAnular = (cotizacion: CotizacionVenta): boolean => {
    const estado = cotizacion.estado || 'Nuevo';
    return estado !== 'Facturado' && estado !== 'Preparado' && estado !== 'Anulado';
  };

  const puedeFacturar = (cotizacion: CotizacionVenta): boolean => {
    const estado = cotizacion.estado || 'Nuevo';
    return estado !== 'Facturado' && estado !== 'Anulado';
  };

  const puedePreparar = (cotizacion: CotizacionVenta): boolean => {
    const estado = cotizacion.estado || 'Nuevo';
    return estado !== 'Preparado' && estado !== 'Anulado' && estado !== 'Facturado';
  };

  const getEstadoChip = (cotizacion: CotizacionVenta) => {
    const estado = cotizacion.estado || 'Nuevo';
    const estadoMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
      'Facturado': { label: 'Facturado', bgColor: '#C6EEBB', textColor: '#000000' },
      'Preparado': { label: 'Preparado', bgColor: '#FFFF00', textColor: '#000000' },
      'Anulado': { label: 'Anulado', bgColor: '#FFCDD2', textColor: '#000000' },
      'Impreso': { label: 'Impreso', bgColor: '#FF80FF', textColor: '#000000' },
      'Nuevo': { label: 'Nuevo', bgColor: '#FFFFD7', textColor: '#000000' }
    };
    return estadoMap[estado] || { label: estado, bgColor: '#FFFFD7', textColor: '#000000' };
  };

  const formatComprobante = (cotizacion: CotizacionVenta & { letra?: string; nroSuc?: number; nroComprobante?: number }) => {
    const letra = (cotizacion as any).letra;
    const nroSuc = (cotizacion as any).nroSuc;
    const nroComprobante = (cotizacion as any).nroComprobante;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) return '-';
    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      return (!Number.isNaN(numeric) ? numeric.toString() : String(value)).padStart(length, '0');
    };
    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();
    if (!letraNormalizada) return '-';
    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  const renderKebabCotizacionItems = (cotizacion: CotizacionVenta) => {
    const menuItems: React.ReactElement[] = [];
    menuItems.push(
      <MenuItem key="ver" onClick={() => { handleKebabClose(); handleCotizacionClick(cotizacion.nroFormulario); }}>
        <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Ver detalle</ListItemText>
      </MenuItem>
    );
    if (esCotizacionEditable(cotizacion)) {
      menuItems.push(
        <MenuItem key="editar" onClick={() => { handleKebabClose(); navigate(`/ventas/cotizaciones/${cotizacion.nroFormulario}/editar`); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
      );
    }
    if (puedeEditarOAnular(cotizacion)) {
      menuItems.push(
        <MenuItem key="anular" onClick={() => { handleKebabClose(); void handleAnular(cotizacion.nroFormulario); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Anular</ListItemText>
        </MenuItem>
      );
    }
    return menuItems;
  };

  const renderActionButtons = (cotizacion: CotizacionVenta) => {
    return (
      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
        {cotizacion.estado !== 'Anulado' && (
          <Tooltip title="Imprimir PDF">
            <IconButton size="small" onClick={(e) => handleImprimirPDF(cotizacion.nroFormulario, e)} disabled={pdfLoading === cotizacion.nroFormulario}
              sx={{ bgcolor: '#2196f3', color: '#fff', borderRadius: 1.5, width: 32, height: 32, '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' }, '&:disabled': { bgcolor: '#ccc' }, transition: 'all 0.2s' }}>
              {pdfLoading === cotizacion.nroFormulario ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PrintIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {puedePreparar(cotizacion) && (
          <Tooltip title="Preparar cotización">
            <IconButton size="small" onClick={(e) => handlePreparar(cotizacion.nroFormulario, e)}
              sx={{ bgcolor: '#9c27b0', color: '#fff', borderRadius: 1.5, width: 32, height: 32, '&:hover': { bgcolor: '#7b1fa2', transform: 'scale(1.1)' }, transition: 'all 0.2s' }}>
              <Inventory2Icon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {puedeFacturar(cotizacion) && (
          <Tooltip title="Facturar">
            <IconButton size="small" onClick={(e) => handleFacturar(cotizacion.nroFormulario, e)}
              sx={{ bgcolor: '#4caf50', color: '#fff', borderRadius: 1.5, width: 32, height: 32, '&:hover': { bgcolor: '#45a049', transform: 'scale(1.1)' }, transition: 'all 0.2s' }}>
              <ReceiptIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Más acciones">
          <IconButton size="small" onClick={(e) => handleKebabOpen(e, cotizacion.nroFormulario)}
            sx={{ borderRadius: 1.5, width: 32, height: 32 }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>Cotizaciones</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SpeedIcon />}
              onClick={handleNuevaCotizacion}
              sx={{
                borderRadius: 2,
                px: 2.5,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' },
              }}
            >
              Nueva Cotización POS
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth placeholder="Buscar por número, nombre o cliente..."
                  value={searchTerm} onChange={handleSearchChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa', '&:hover': { bgcolor: '#f5f5f5' }, '&.Mui-focused': { bgcolor: '#ffffff' } } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#666' }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <SearchableSelect
                  label="Estado"
                  allowEmpty
                  emptyOptionLabel="Todos"
                  value={filtroEstado || null}
                  onChange={(v) => { setFiltroEstado(v ?? ''); setPage(0); }}
                  options={[
                    { value: 'Nuevo', label: 'Nuevo' },
                    { value: 'Impreso', label: 'Impreso' },
                    { value: 'Facturado', label: 'Facturado' },
                    { value: 'Preparado', label: 'Preparado' },
                    { value: 'Anulado', label: 'Anulado' },
                  ]}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': { bgcolor: '#f5f5f5' },
                      '&.Mui-focused': { bgcolor: '#ffffff' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth label="Fecha Desde" type="date" value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth label="Fecha Hasta" type="date" value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          {isMobile ? (
            <Box>
              {items.map((cotizacion) => {
                const estadoChip = getEstadoChip(cotizacion);
                return (
                  <Card key={cotizacion.nroFormulario} sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' } }}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Cotización #{cotizacion.nroFormulario}</Typography>
                          <Chip label={estadoChip.label} size="small" sx={{ bgcolor: estadoChip.bgColor, color: estadoChip.textColor, fontWeight: 500, borderRadius: 1.5 }} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">Fecha: {new Date(cotizacion.fechaEmision).toLocaleDateString()}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Vencimiento: {formatFechaVencimDisplay(cotizacion.fechaVencim ?? cotizacion.fechaEmision)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Cliente: {cotizacion.nroCliente} - {cotizacion.nombre}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>Importe Total: {formatCurrency(calcularTotal(cotizacion))}</Typography>
                        {renderActionButtons(cotizacion)}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              <TablePagination rowsPerPageOptions={[5, 10, 20]} component="div" count={totalRecords} rowsPerPage={rowsPerPage} page={page}
                onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Filas por página" />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vencimiento</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((cotizacion) => {
                    const estadoChip = getEstadoChip(cotizacion);
                    return (
                      <TableRow key={cotizacion.nroFormulario} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                        <TableCell sx={{ fontWeight: 500 }}>{cotizacion.nroFormulario}</TableCell>
                        <TableCell>{new Date(cotizacion.fechaEmision).toLocaleDateString()}</TableCell>
                        <TableCell>{formatFechaVencimDisplay(cotizacion.fechaVencim ?? cotizacion.fechaEmision)}</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography
                            component="span"
                            sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                            onClick={(e) => handleAbrirCotizacionPos(cotizacion.nroFormulario, e)}
                          >
                            {formatComprobante(cotizacion)}
                          </Typography>
                        </TableCell>
                        <TableCell>{cotizacion.nroCliente}</TableCell>
                        <TableCell>{cotizacion.nombre}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>{formatCurrency(calcularTotal(cotizacion))}</TableCell>
                        <TableCell>
                          <Chip label={estadoChip.label} size="small" sx={{ bgcolor: estadoChip.bgColor, color: estadoChip.textColor, fontWeight: 500, borderRadius: 1.5, minWidth: 90 }} />
                        </TableCell>
                        <TableCell align="center">{renderActionButtons(cotizacion)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination rowsPerPageOptions={[5, 10, 20]} component="div" count={totalRecords} rowsPerPage={rowsPerPage} page={page}
                onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Filas por página" sx={{ borderTop: '1px solid #e0e0e0' }} />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={kebabAnchor?.el}
        open={Boolean(kebabAnchor)}
        onClose={handleKebabClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {currentKebabCotizacion && renderKebabCotizacionItems(currentKebabCotizacion)}
      </Menu>

      {pdfUrl && selectedCotizacion && (
        <PDFViewer pdfUrl={pdfUrl} onClose={handleClosePDF} title={`Cotización #${selectedCotizacion}`} documentId={`${selectedCotizacion}`} documentType="cotizacion" />
      )}
    </Box>
  );
};

export default Cotizaciones;
